import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiOutlineDuplicate, HiOutlineHashtag } from 'react-icons/hi';
import { FaEthereum, FaWallet } from 'react-icons/fa';
import { TbBrandBinance, TbWallet } from 'react-icons/tb';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';
import { RiSafe2Line, RiShieldKeyholeLine } from 'react-icons/ri';
import { useWallet, readStoredUpline, isWalletAddress } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { detectWalletAvailability, sendBnb, shortAddress } from '../services/wallet';
import Modal from './Modal';
import './ConnectModal.css';

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function formatBnb(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0';
  return amount.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function registerWalletError(err) {
  const text = String(err?.message || '');
  const cancelled =
    err?.code === 4001 ||
    /user rejected|user denied|cancelled|denied transaction/i.test(text);
  if (cancelled) {
    const out = new Error('Registration cancelled.');
    out.code = 4001;
    return out;
  }
  if (/network fee/i.test(text)) {
    return new Error('Your wallet needs a little BNB for the network.');
  }
  if (/does not have enough/i.test(text)) {
    return new Error('Your wallet does not have enough BNB to register.');
  }
  if (/payment|could not be sent/i.test(text)) {
    return new Error('Registration could not be completed. Check your BNB balance and try again.');
  }
  return err;
}

const WALLETS = [
  {
    id: 'metamask',
    name: 'MetaMask',
    hint: 'Browser extension & app',
    Icon: FaEthereum,
    color: '#F6851B',
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    hint: 'Mobile & browser',
    Icon: RiShieldKeyholeLine,
    color: '#0500FF',
  },
  {
    id: 'binance',
    name: 'Binance Wallet',
    hint: 'BNB Chain wallet',
    Icon: TbBrandBinance,
    color: '#F0B90B',
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    hint: 'Extension & app',
    Icon: MdOutlineAccountBalanceWallet,
    color: '#0052FF',
  },
  {
    id: 'okx',
    name: 'OKX Wallet',
    hint: 'Web3 multi-chain',
    Icon: FaWallet,
    color: '#FFFFFF',
  },
  {
    id: 'bitget',
    name: 'Bitget Wallet',
    hint: 'Formerly BitKeep',
    Icon: TbWallet,
    color: '#00F0FF',
  },
  {
    id: 'tokenpocket',
    name: 'TokenPocket',
    hint: 'Multi-chain wallet',
    Icon: FaWallet,
    color: '#2980FE',
  },
  {
    id: 'safepal',
    name: 'SafePal',
    hint: 'Hardware & software',
    Icon: RiSafe2Line,
    color: '#4A21EF',
  },
  {
    id: 'injected',
    name: 'Browser Wallet',
    hint: 'Any injected provider',
    Icon: FaWallet,
    color: '#00D2EF',
  },
];

export default function ConnectModal() {
  const {
    connectOpen,
    connectReason,
    connectMode,
    setConnectMode,
    pendingWalletId,
    setPendingWalletId,
    closeConnect,
    connectAndAuth,
    register,
    referralUpline,
    setReferralUpline,
    setError,
    error,
    config,
    address,
  } = useWallet();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(pendingWalletId || 'injected');
  const [upline, setUpline] = useState('');
  const [localError, setLocalError] = useState('');
  const [availability, setAvailability] = useState({});
  const [payStep, setPayStep] = useState('transfer');
  const [manualHash, setManualHash] = useState('');
  const navigate = useNavigate();

  const registerFee = Number(config?.register_fee_bnb);
  const registerWallet = String(config?.register_wallet || '').trim();
  const feeRequired = Number.isFinite(registerFee) && registerFee > 0;

  useEffect(() => {
    if (!connectOpen) {
      setLocalError('');
      setBusy(false);
      return;
    }
    setAvailability(detectWalletAvailability());
    setSelectedWallet(pendingWalletId || 'injected');
    if (connectMode === 'register') {
      setUpline(referralUpline || readStoredUpline() || '');
      setPayStep('transfer');
      setManualHash('');
      toast.info('Wallet not registered. Enter your refer address to continue.');
    }
    if (connectMode === 'pay') {
      setPayStep('transfer');
    }
    if (error && connectMode === 'picker') {
      setLocalError(error);
      toast.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectOpen, connectMode]);

  async function runConnect(walletId) {
    setBusy(true);
    setLocalError('');
    setSelectedWallet(walletId);
    setPendingWalletId(walletId);
    try {
      const result = await connectAndAuth(walletId);
      if (result.status === 'needs_register') {
        setConnectMode('register');
        setUpline(referralUpline || readStoredUpline() || '');
        toast.info('Wallet not registered. Enter your refer address to continue.');
      } else {
        toast.success('Wallet connected');
        navigate('/dashboard');
      }
    } catch (e) {
      setLocalError(e.message);
      setError(e.message);
      toast.error(e.message || 'Connection failed');
      setConnectMode('picker');
    } finally {
      setBusy(false);
    }
  }

  async function onPickWallet(walletId) {
    await runConnect(walletId);
  }

  async function finishRegister(txHash = '') {
    await register(upline.trim(), selectedWallet || pendingWalletId || 'injected', txHash);
    toast.success('Registration complete');
    navigate('/dashboard');
  }

  async function ensureReferReady() {
    const refer = upline.trim();
    if (!isWalletAddress(refer)) {
      const err = new Error('Enter a valid refer wallet address (0x…)');
      err.code = 'INVALID_UPLINE';
      throw err;
    }
    const wallet = String(address || '').trim();
    if (!isWalletAddress(wallet)) {
      throw new Error('Connect your wallet to continue');
    }
    if (refer.toLowerCase() === wallet.toLowerCase()) {
      const err = new Error('Upline cannot be your own wallet');
      err.code = 'INVALID_UPLINE';
      throw err;
    }
    await api.checkRegister(wallet, refer);
    setReferralUpline(refer);
    return refer;
  }

  function returnToRefer(message) {
    setPayStep('transfer');
    setConnectMode('register');
    if (message) {
      setLocalError(message);
      setError(message);
      toast.error(message);
    }
  }

  async function onRegister(e) {
    e.preventDefault();
    if (feeRequired && !isWalletAddress(registerWallet)) {
      const msg = 'Registration wallet is not set. Ask admin to set it.';
      setLocalError(msg);
      toast.error(msg);
      return;
    }

    setLocalError('');
    setBusy(true);
    try {
      await ensureReferReady();
      if (feeRequired) {
        setPayStep('transfer');
        setManualHash('');
        setConnectMode('pay');
        return;
      }
      await finishRegister();
    } catch (err) {
      setLocalError(err.message);
      setError(err.message);
      toast.error(err.message || 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  async function payWithWallet() {
    if (!feeRequired || !isWalletAddress(registerWallet)) return;
    if (!isWalletAddress(upline.trim())) {
      returnToRefer('Enter a valid refer wallet address (0x…)');
      return;
    }
    setBusy(true);
    setLocalError('');
    try {
      await ensureReferReady();
      setPayStep('approve');
      const { txHash } = await sendBnb({
        to: registerWallet,
        amount: registerFee,
        onSubmitted: () => setPayStep('confirming'),
      });
      setPayStep('registering');
      await finishRegister(txHash);
    } catch (err) {
      if (err.code === 'INVALID_UPLINE' || /refer|upline/i.test(err.message || '')) {
        returnToRefer(err.message);
        return;
      }
      const friendly = registerWalletError(err);
      setPayStep('transfer');
      setLocalError(friendly.message);
      setError(friendly.message);
      if (friendly.code === 4001) toast.info(friendly.message);
      else toast.error(friendly.message || 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  async function submitManualHash(event) {
    event.preventDefault();
    const hash = manualHash.trim();
    if (!/^0x[a-fA-F0-9]{64}$/.test(hash)) {
      const msg = 'Enter the 66-character transaction hash that starts with 0x.';
      setLocalError(msg);
      toast.error(msg);
      return;
    }
    if (!isWalletAddress(upline.trim())) {
      returnToRefer('Enter a valid refer wallet address (0x…)');
      return;
    }
    setBusy(true);
    setLocalError('');
    try {
      await ensureReferReady();
      setPayStep('registering');
      await finishRegister(hash);
    } catch (err) {
      if (err.code === 'INVALID_UPLINE' || /refer|upline/i.test(err.message || '')) {
        returnToRefer(err.message);
        return;
      }
      setPayStep('transfer');
      setLocalError(err.message);
      setError(err.message);
      toast.error(err.message || 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  const hashLooksValid = /^0x[a-fA-F0-9]{64}$/.test(manualHash);
  const referReady = isWalletAddress(upline);
  const paying = connectMode === 'pay';
  const title = paying || connectMode === 'register' ? 'Register wallet' : 'Choose a wallet';
  const subtitle = paying
    ? `Confirm ${formatBnb(registerFee)} BNB with your wallet to create this account.`
    : connectMode === 'register'
      ? 'This wallet is new. Enter your refer (upline) wallet address to register.'
      : connectReason ||
        'Popular dApp wallets on BNB Smart Chain. Detected wallets are marked ready.';

  const footer = paying ? (
    payStep === 'transfer' ? (
      <>
        <button className="btn btn--primary" type="button" onClick={payWithWallet} disabled={busy || !referReady}>
          <TbWallet />
          Connect & Register
        </button>
        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => {
            setConnectMode('register');
            setLocalError('');
          }}
          disabled={busy}
        >
          <HiArrowLeft /> Back
        </button>
      </>
    ) : null
  ) : connectMode === 'register' ? (
    <button
      className="btn btn--ghost"
      type="button"
      onClick={() => {
        setConnectMode('picker');
        setLocalError('');
      }}
      disabled={busy}
    >
      <HiArrowLeft /> Back to wallets
    </button>
  ) : null;

  return (
    <Modal
      open={connectOpen}
      onClose={busy || payStep === 'approve' || payStep === 'confirming' || payStep === 'registering' ? undefined : closeConnect}
      eyebrow="Wallet Connect"
      title={title}
      subtitle={subtitle}
      footer={footer}
    >
      {connectMode === 'picker' ? (
        <div className="wallet-list">
          {WALLETS.map((wallet, index) => {
            const Icon = wallet.Icon;
            const detected =
              !!availability[wallet.id] || (wallet.id === 'injected' && availability.injected);
            return (
              <motion.button
                key={wallet.id}
                type="button"
                className="wallet-item"
                disabled={busy}
                onClick={() => onPickWallet(wallet.id)}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.04 }}
                whileHover={{ scale: 1.02, x: 2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="wallet-item__icon" style={{ color: wallet.color }}>
                  <Icon size={22} />
                </span>
                <span className="wallet-item__meta">
                  <strong>{wallet.name}</strong>
                  <small>{wallet.hint}</small>
                </span>
                <span className={`wallet-item__status${detected ? ' is-on' : ''}`}>
                  {detected ? 'Detected' : 'Install'}
                </span>
              </motion.button>
            );
          })}
          {busy ? <p className="modal__busy">Connecting… approve in your wallet</p> : null}
          {localError ? <p className="modal__error">{localError}</p> : null}
        </div>
      ) : null}

      {connectMode === 'register' ? (
        <form className="modal__form" onSubmit={onRegister}>
          <p>
            Login failed because this wallet is not registered. Provide a refer wallet address to
            create your account.
          </p>
          <label>
            Refer wallet address
            <input
              value={upline}
              onChange={(e) => setUpline(e.target.value)}
              placeholder="0x…"
              required
              spellCheck={false}
              autoComplete="off"
            />
          </label>
          <button className="btn btn--primary" type="submit" disabled={busy || !referReady}>
            {busy ? 'Checking refer…' : 'Connect & Register'}
          </button>
          {localError ? <p className="modal__error">{localError}</p> : null}
        </form>
      ) : null}

      {connectMode === 'pay' ? (
        payStep === 'transfer' ? (
          <div className="stake-transfer">
            <div className="stake-preview">
              <div>
                <span>Registration</span>
                <strong>{formatBnb(registerFee)} BNB</strong>
              </div>
              <div>
                <span>From</span>
                <strong>{shortAddress(address) || 'Connected wallet'}</strong>
              </div>
            </div>
            <label className="stake-transfer__label" htmlFor="register-fee-wallet">To</label>
            <div className="stake-transfer__address">
              <code id="register-fee-wallet">{registerWallet}</code>
              <button
                type="button"
                aria-label="Copy registration wallet"
                onClick={() => copyText(registerWallet).then((ok) => ok && toast.success('Address copied'))}
              >
                <HiOutlineDuplicate />
              </button>
            </div>
            <p className="muted stake-transfer__hint">
              Use BNB from this connected wallet on BNB Smart Chain. Your account is created after confirmation.
            </p>
            <form className="register-hash" onSubmit={submitManualHash}>
              <div className="register-hash__head">
                <span className="register-hash__ico" aria-hidden="true">
                  <HiOutlineHashtag />
                </span>
                <div>
                  <label htmlFor="register-tx-hash">Transaction hash</label>
                  <p>Already confirmed? Paste the hash to finish registration.</p>
                </div>
              </div>
              <div className={`register-hash__box${hashLooksValid ? ' is-ready' : ''}`}>
                <input
                  id="register-tx-hash"
                  value={manualHash}
                  onChange={(event) => setManualHash(event.target.value.trim())}
                  placeholder="0x…"
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="text"
                  aria-invalid={manualHash ? !hashLooksValid : undefined}
                />
                <button
                  type="submit"
                  className="register-hash__btn"
                  disabled={busy || !hashLooksValid || !referReady}
                >
                  Verify
                </button>
              </div>
              <p className="register-hash__meta">
                {manualHash
                  ? hashLooksValid
                    ? 'Hash looks ready'
                    : `${manualHash.length}/66 characters`
                  : '66 characters, starts with 0x'}
              </p>
            </form>
            {localError ? <p className="modal__error">{localError}</p> : null}
          </div>
        ) : (
          <div className="modal__connecting">
            <div className="modal__spinner" />
            <p className="modal__busy">
              {payStep === 'approve'
                ? 'Approve in your wallet…'
                : payStep === 'confirming'
                  ? 'Waiting for confirmation…'
                  : 'Creating your account…'}
            </p>
          </div>
        )
      ) : null}
    </Modal>
  );
}
